import React from 'react';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background: ${({ theme }) => theme.colors.background.dark};
  color: ${({ theme }) => theme.colors.text.inverse};
  padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.content.base} ${theme.spacing.xl}`};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.content.narrow} ${theme.spacing.lg}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.content.narrow} ${theme.spacing.base}`};
  }
`;

const FooterContent = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
`;

const FooterMain = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: ${({ theme }) => theme.spacing['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
    text-align: center;
  }
`;

const BrandSection = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-column: 1 / -1;
    text-align: center;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const BrandName = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.brand};
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.white};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const BrandTagline = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.inverse};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  opacity: 0.9;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const FooterSection = styled.div``;

const SectionTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.white};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const FooterNav = styled.nav``;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterListItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const FooterLink = styled.a`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.inverse};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.base};
  opacity: 0.8;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.white};
    opacity: 1;
    text-decoration: underline;
    text-decoration-color: ${({ theme }) => theme.colors.white};
    text-underline-offset: 0.2em;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.white};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.base};
  margin-top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.inverse};
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.base};
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.primary.main};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.white};
    outline-offset: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: ${({ theme }) => theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing.lg};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.base};
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.inverse};
  margin: 0;
  opacity: 0.7;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const LegalLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const LegalLink = styled.a`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.inverse};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.base};
  opacity: 0.7;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.white};
    opacity: 1;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.white};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const Disclaimer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  margin-top: ${({ theme }) => theme.spacing.lg};
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.base};
  }
`;

const DisclaimerText = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.inverse};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  opacity: 0.6;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const Footer = ({ onOpenPrivacyPolicy, onOpenTermsOfService }) => {
  console.log('Footer updated - contrast and colors fixed');
  const currentYear = new Date().getFullYear();

  const handleScrollToTop = (e) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleScrollToSection = (sectionId) => (e) => {
    e.preventDefault();
    const target = document.getElementById(sectionId);
    if (target) {
      const headerHeight = 80;
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterMain>
          <BrandSection>
            <BrandName>Antony Haines</BrandName>
            <BrandTagline>
              Fractional CTO for Manufacturing Excellence. Transforming operational crises into 
              competitive advantages through strategic technology leadership and Fortune 500-proven 
              results in IT/OT convergence.
            </BrandTagline>
            <SocialLinks>
              <FooterLink 
                href="https://linkedin.com/in/antonyhaines" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                LinkedIn Profile
              </FooterLink>
            </SocialLinks>
          </BrandSection>

          <FooterSection>
            <SectionTitle>Navigation</SectionTitle>
            <FooterNav>
              <FooterList>
                <FooterListItem>
                  <FooterLink href="#about" onClick={handleScrollToSection('about')}>
                    About Antony
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#services" onClick={handleScrollToSection('services')}>
                    Strategic Services
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#case-study" onClick={handleScrollToSection('case-study')}>
                    Proven Results
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#methodology" onClick={handleScrollToSection('methodology')}>
                    Methodology
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#qualification" onClick={handleScrollToSection('qualification')}>
                    Strategic Fit
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#contact" onClick={handleScrollToSection('contact')}>
                    Contact
                  </FooterLink>
                </FooterListItem>
              </FooterList>
            </FooterNav>
          </FooterSection>

          <FooterSection>
            <SectionTitle>Services</SectionTitle>
            <FooterNav>
              <FooterList>
                <FooterListItem>
                  <FooterLink href="#services" onClick={handleScrollToSection('services')}>
                    Free Zoom Consultation
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#services" onClick={handleScrollToSection('services')}>
                    Deep Assessment
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#services" onClick={handleScrollToSection('services')}>
                    Discovery Project
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#services" onClick={handleScrollToSection('services')}>
                    Fractional CTO
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#case-study" onClick={handleScrollToSection('case-study')}>
                    Case Studies
                  </FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#methodology" onClick={handleScrollToSection('methodology')}>
                    AI-Assisted Intelligence
                  </FooterLink>
                </FooterListItem>
              </FooterList>
            </FooterNav>
          </FooterSection>
        </FooterMain>

        <FooterBottom>
          <Copyright>
            © {currentYear} Antony Haines Consulting. All rights reserved.
          </Copyright>
          <LegalLinks>
            <LegalLink 
              href="#privacy" 
              onClick={(e) => {
                e.preventDefault();
                onOpenPrivacyPolicy();
              }}
            >
              Privacy Policy
            </LegalLink>
            <LegalLink 
              href="#terms" 
              onClick={(e) => {
                e.preventDefault();
                onOpenTermsOfService();
              }}
            >
              Terms of Service
            </LegalLink>
            <LegalLink href="#" onClick={handleScrollToTop}>
              Back to Top
            </LegalLink>
          </LegalLinks>
        </FooterBottom>

        <Disclaimer>
          <DisclaimerText>
            Strategic technology consulting services. Past performance does not guarantee future results. 
            All case studies represent actual client outcomes with appropriate confidentiality protections. 
            Zoom consultation and engagement terms subject to mutual agreement and strategic fit assessment.
          </DisclaimerText>
        </Disclaimer>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;